import React, { useEffect, useState } from 'react';
import { Badge, Button, DataTable, Icon } from '@aidkitorg/component-library';
import { ColumnDef } from '@tanstack/react-table';
import { BadgeVariant } from '@aidkitorg/component-library/dist/types/src/Badge';
import { usePayments } from '../../../Contexts/PaymentsContext';

export type PaymentBatchRecipient = {
  uuid: string,
  firstName: string,
  lastName: string,
  email: string,
  phone: string,
  queuedDate: Date,
  paymentMethod: '' | 'direct_deposit' | 'ach',
  paymentStatus: '' | 'paused' | 'pending' | 'paid' | 'failed',
  paymentDetails: string,
  treatmentControlGroup: 'treatment' | 'control'
}

const PAYMENT_BATCH_RECIPIENT_DETAILS_COLUMNS: ColumnDef<PaymentBatchRecipient>[] = [
  {
    id: 'uuid',
    accessorKey: "uuid",
    enablePinning: true,
    cell: ({ row }) => {
      const uuid = row.original.uuid
      const [copied, setCopied] = useState(false)
  
      const handleCopy = async () => {
        try {
          await navigator.clipboard.writeText(uuid)
          setCopied(true)
          setTimeout(() => setCopied(false), 2000)
        } catch (err) {
          console.error("Failed to copy: ", err)
        }
      }
  
      return (
        <div className="flex items-center gap-2">
          <span className="max-w-[160px]">{uuid}</span>
          <Button 
            icon={
              <Icon role="decorative" name={copied ? "CheckIcon" : "ClipboardDocumentIcon"}/>
            }
            variant="secondary" 
            size="sm"
            aria-label="Copy UUID to clipboard"
            onClick={handleCopy}
          />
        </div>
      )
    }
  },
  {
    accessorKey: "firstName",
    header: "First Name",
  },
  {
    accessorKey: "lastName",
    header:"Last Name",
  },
  {
    accessorKey: "email",
    cell: ({ row }) => (
      <div className="truncate">{row.original.email}</div>
    )
  },
  {
    accessorKey: "phone",
    header: "Phone Number",
  },
  {
    accessorKey: "queuedDate",
    header: "Queued Date",
    cell: ({ row }) => {
      const date = row.original.queuedDate
      return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(date)
    },
  },
  {
    accessorKey: "paymentMethod",
    header: "Payment Method",
    cell: ({ row }) => {
      const paymentMethod = row.original.paymentMethod
  
      // TODO: update with all types of payments
      let paymentMethodCopy = ''
      switch(paymentMethod) {
        case 'direct_deposit': 
          paymentMethodCopy = 'Direct Deposit'
          break;
        case 'ach':
          paymentMethodCopy = 'ACH'
          break;
        default:
          paymentMethodCopy = '--'
      }
  
      return <span>{paymentMethodCopy}</span>
    }
  },
  {
    accessorKey: "paymentStatus",
    header: "Payment Status",
    cell: ({ row }) => {
      const status = row.original.paymentStatus
  
      if (status === '') {
        return <span>--</span>
      }
  
      let variant: BadgeVariant;
      let copy = '';
  
      switch (status) {
        case 'paused':
          variant = 'warning'
          copy = "Paused"
          break;
        case 'pending':
          variant = 'info'
          copy = "Pending"
          break;
        case 'paid':
          variant = 'success'
          copy = "Paid"
          break;
        case 'failed':
          variant = 'error'
          copy = "Failed"
          break;
      }
  
      return (
        <Badge variant={variant} >{copy}</Badge>
      )
    },
  },
  {
    accessorKey: 'paymentDetails',
    header: "Payment Details",
  },
  {
    accessorKey: "treatmentControlGroup",
    header: "Treatment or Control",
    cell: ({ row }) => {
      const status = row.original.treatmentControlGroup;
  
      // TODO: update badge with new colors
      return <Badge variant={status === 'treatment' ? 'info' : 'neutral'}>{status === 'treatment' ? 'Treatment' : 'Control'}</Badge>
    }
  },
  {
    id: "view-application",
    header: "View Application",
    cell: ({ row }) => {
      return <Button href='/' variant="link" openInNewWindow>
        <div className="flex gap-2 items-center">
          View Application 
          <Icon size="sm" role="decorative" name="ArrowTopRightOnSquareIcon" />
        </div>
      </Button>
    },
    enableSorting: false,
    enablePinning: true,
  }
]

export const PaymentBatchRecipientsDataTable: React.FC<{}> = ({}) => {
  const [paymentRecipientDetails, setPaymentRecipientDetails] = useState<Array<PaymentBatchRecipient>>([])
  const { getRecipientDetailsByBatch } = usePayments();
  
  // TODO: update with real batch name
  useEffect(() => {
    const recipientDetails = getRecipientDetailsByBatch("BATCH_NAME")    
    setPaymentRecipientDetails(recipientDetails)
  }, [])
    
  return (
    <DataTable 
      columns={PAYMENT_BATCH_RECIPIENT_DETAILS_COLUMNS}
      data={paymentRecipientDetails}
      enableSearch
      searchLabel='Search recipients...'
      columnPinningState={{
        left: ['uuid'],
        right: ['view-application']
      }}
      resizableColumns
    />
  )
}
  